import { QueryClient } from "@tanstack/react-query";
import { ApiError, apiFetch } from "./api";
import type {
  AuthCheck,
  ChangedSurface,
  CaseStability,
  CoverageTarget,
  CoverageTrendEntry,
  Finding,
  FindingCreate,
  FindingFunnel,
  FindingUpdate,
  Gap,
  GoNoGo,
  GreenWallOut,
  HumanSitting,
  LastTested,
  LiveReport,
  Notification,
  NotificationKind,
  Owner,
  Page,
  PendingReview,
  Policy,
  PolicyCreate,
  PolicyUpdate,
  Presence,
  PublishedReport,
  Readiness,
  ReconcileReport,
  RegistryCatalog,
  RegistryPayload,
  RegistryResolution,
  ReductionSummary,
  ResultDetail,
  ReviewBatchResult,
  Run,
  RunCompareOut,
  RunDetail,
  RunFacets,
  RunSet,
  RunSignals,
  ScenarioDetail,
  SearchResults,
  SectionTrendEntry,
  SittingAnswer,
  TagActivity,
  TriageQueueItem,
  TriageVerdict,
} from "./types";

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 10_000,
      retry: 1,
    },
  },
});

export const api = {
  authCheck: () => apiFetch<AuthCheck>("/auth/check"),
  listRuns: (params: URLSearchParams) => apiFetch<Page<Run>>(`/runs?${params}`),
  getRunFacets: () => apiFetch<RunFacets>("/runs/facets"),
  getRun: (id: string) => apiFetch<RunDetail>(`/runs/${id}`),
  listTargets: (runId: string) => apiFetch<CoverageTarget[]>(`/runs/${runId}/coverage-targets`),
  listScenarios: (runId: string) => apiFetch<ScenarioDetail[]>(`/runs/${runId}/scenarios`),
  listSets: (runId: string) => apiFetch<RunSet[]>(`/runs/${runId}/sets`),
  listRunStability: (runId: string) => apiFetch<CaseStability[]>(`/runs/${runId}/stability`),
  getGoNoGo: (runId: string) => apiFetch<GoNoGo>(`/runs/${runId}/go-no-go`),
  getReadiness: (runId: string) => apiFetch<Readiness>(`/runs/${runId}/readiness`),
  getRunSignals: (ids: string[]) => {
    const params = new URLSearchParams();
    for (const id of ids) params.append("run_id", id);
    return apiFetch<RunSignals[]>(`/runs/signals?${params}`);
  },
  listPendingForRun: (runId: string, params = new URLSearchParams({ limit: "200" })) =>
    apiFetch<Page<TriageVerdict>>(`/runs/${runId}/verdicts/pending-review?${params}`),
  listPendingReviews: (params: URLSearchParams) =>
    apiFetch<Page<PendingReview>>(`/verdicts/pending-review?${params}`),
  getTriageQueue: (params: URLSearchParams) =>
    apiFetch<Page<TriageQueueItem>>(`/triage/queue?${params}`),
  getResult: (id: string) => apiFetch<ResultDetail>(`/results/${id}`),
  getVerdict: (resultId: string) =>
    apiFetch<TriageVerdict | null>(`/results/${resultId}/verdict`).catch((err) => {
      // No verdict yet is the common case, not an error the caller must handle.
      if (err instanceof ApiError && err.status === 404) return null;
      throw err;
    }),
  reviewVerdict: (id: string, reviewed_by: string) =>
    apiFetch<TriageVerdict>(`/verdicts/${id}/review`, {
      method: "PATCH",
      body: JSON.stringify({ reviewed_by }),
    }),
  reviewBatch: (verdict_ids: string[], reviewed_by: string) =>
    apiFetch<ReviewBatchResult>("/verdicts/review-batch", {
      method: "POST",
      body: JSON.stringify({ verdict_ids, reviewed_by }),
    }),
  upsertVerdict: (
    resultId: string,
    body: {
      verdict: string;
      spec_comparison: Record<string, unknown>;
      confidence?: number | null;
      reasoning?: string | null;
    },
  ) =>
    apiFetch<TriageVerdict>(`/results/${resultId}/verdict`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  getGaps: (runId: string) => apiFetch<Gap[]>(`/runs/${runId}/gaps`),
  getSummary: (runId: string) => apiFetch<ReductionSummary>(`/runs/${runId}/summary`),
  getPolicy: (id: string) => apiFetch<Policy>(`/policies/${id}`),
  listPolicies: (params: URLSearchParams) => apiFetch<Page<Policy>>(`/policies?${params}`),
  createPolicy: (body: PolicyCreate) =>
    apiFetch<Policy>("/policies", { method: "POST", body: JSON.stringify(body) }),
  updatePolicy: (id: string, body: PolicyUpdate) =>
    apiFetch<Policy>(`/policies/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  listFindings: (runId: string, params = new URLSearchParams({ limit: "200" })) =>
    apiFetch<Page<Finding>>(`/runs/${runId}/findings?${params}`),
  getChangedSurface: (runId: string) =>
    apiFetch<ChangedSurface>(`/runs/${runId}/exploration/changed-surface`),
  listUngraduatedFindings: (params: URLSearchParams) =>
    apiFetch<Page<Finding>>(`/findings/ungraduated?${params}`),
  getFindingFunnel: (params = new URLSearchParams()) =>
    apiFetch<FindingFunnel>(`/findings/funnel?${params}`),
  updateFinding: (id: string, body: FindingUpdate) =>
    apiFetch<Finding>(`/findings/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  recordFinding: (runId: string, body: FindingCreate) =>
    apiFetch<Finding>(`/runs/${runId}/findings`, { method: "POST", body: JSON.stringify(body) }),
  getLiveReport: (runId: string, verbosity: string) =>
    apiFetch<LiveReport>(`/runs/${runId}/report?verbosity=${verbosity}`),
  publishReport: (runId: string, verbosity: string) =>
    apiFetch<PublishedReport>(`/runs/${runId}/report/publish`, {
      method: "POST",
      body: JSON.stringify({ verbosity }),
    }),
  listReports: (runId: string) => apiFetch<Page<PublishedReport>>(`/runs/${runId}/reports`),
  getGreenWall: (params: URLSearchParams) => apiFetch<GreenWallOut>(`/trends/green-wall?${params}`),
  getCoverageTrend: (params: URLSearchParams) =>
    apiFetch<Page<CoverageTrendEntry>>(`/trends/coverage?${params}`),
  getSectionTrend: (specSectionId: string, params: URLSearchParams) =>
    apiFetch<Page<SectionTrendEntry>>(
      `/trends/section/${encodeURIComponent(specSectionId)}?${params}`,
    ),
  compareRuns: (runId: string, otherRunId: string) =>
    apiFetch<RunCompareOut>(`/runs/${runId}/compare/${otherRunId}`),
  getRegistry: () => apiFetch<RegistryPayload>("/registry"),
  resolveRegistryTerm: (q: string, catalog: RegistryCatalog) =>
    apiFetch<RegistryResolution>(`/registry/resolve?${new URLSearchParams({ q, catalog })}`),
  getLastTested: (q: string, catalog: RegistryCatalog) =>
    apiFetch<LastTested>(`/registry/last-tested?${new URLSearchParams({ q, catalog })}`),
  getTagActivity: (catalog: RegistryCatalog) =>
    apiFetch<TagActivity>(`/registry/activity?catalog=${catalog}`),
  reconcileRuns: (body: { apply: boolean; action: string }) =>
    apiFetch<ReconcileReport>("/runs/reconcile", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  orphanRun: (runId: string, detail?: string | null) =>
    apiFetch<Run>(`/runs/${runId}/orphan`, {
      method: "POST",
      body: JSON.stringify({ detail: detail ?? null }),
    }),
  abortRun: (runId: string) =>
    apiFetch<Run>(`/runs/${runId}/abort`, { method: "POST" }),

  // --- Phase 5: ownership, presence, inbox, human sittings -----------------
  claimRun: (runId: string, role = "owner") =>
    apiFetch<Owner>(`/runs/${runId}/claim`, {
      method: "POST",
      body: JSON.stringify({ role }),
    }),
  assignRun: (runId: string, actor: string, role = "owner") =>
    apiFetch<Owner>(`/runs/${runId}/assign`, {
      method: "POST",
      body: JSON.stringify({ actor, role }),
    }),
  unassignRun: (runId: string, actor: string, role?: string) =>
    apiFetch<{ removed: number }>(
      `/runs/${runId}/owners/${encodeURIComponent(actor)}${role ? `?role=${role}` : ""}`,
      { method: "DELETE" },
    ),
  listOwners: (runId: string) => apiFetch<Owner[]>(`/runs/${runId}/owners`),
  getHumanSitting: (runId: string) => apiFetch<HumanSitting>(`/runs/${runId}/human-sitting`),
  answerHumanSitting: (runId: string, results: SittingAnswer[]) =>
    apiFetch<HumanSitting & { recorded: number; closed_notification_ids: string[] }>(
      `/runs/${runId}/human-sitting/results`,
      { method: "POST", body: JSON.stringify({ results }) },
    ),

  listPresence: () => apiFetch<Presence[]>("/presence"),
  heartbeatPresence: (body: {
    connection_id: string;
    surface?: string;
    run_id?: string | null;
    scenario_id?: string | null;
  }) =>
    apiFetch<Presence>("/presence/heartbeat", { method: "POST", body: JSON.stringify(body) }),

  listInbox: (params: URLSearchParams) => apiFetch<Page<Notification>>(`/inbox?${params}`),
  raiseNotification: (body: {
    kind: NotificationKind;
    title: string;
    actor?: string;
    pool?: string;
    body?: string;
    action_url?: string;
    run_id?: string;
    priority?: number;
    dedupe_key?: string;
  }) => apiFetch<{ notification: Notification; created: boolean }>("/inbox", {
    method: "POST",
    body: JSON.stringify(body),
  }),
  seenNotification: (id: string) =>
    apiFetch<Notification>(`/inbox/${id}/seen`, { method: "POST" }),
  actNotification: (id: string, resolution?: Record<string, unknown>) =>
    apiFetch<Notification>(`/inbox/${id}/act`, {
      method: "POST",
      body: JSON.stringify({ resolution: resolution ?? null }),
    }),
  dismissNotification: (id: string) =>
    apiFetch<Notification>(`/inbox/${id}/dismiss`, { method: "POST" }),

  // --- Phase 6: palette search ---------------------------------------------
  search: (q: string, limit = 6) =>
    apiFetch<SearchResults>(`/search?${new URLSearchParams({ q, limit: String(limit) })}`),
};
